import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Controller } from 'react-hook-form';
import { mediumRoundedCorners } from '../RoundedCorners';

const FileUploadWrapper = styled.div`
  margin: 0 0 0 4px;
`;

export const FileUploadLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts?.secondary};
  font-size: ${({ theme }) => theme.sizes?.h6};
  color: ${({ theme }) => theme.colors?.blackHigh};
  margin: 0 0 6px 2px;
  display: block;
  opacity: 1;
  line-height: 1;
`;

export const FileUploadHelpText = styled.div`
  font-family: ${({ theme }) => theme.fonts?.secondary};
  font-size: ${({ theme }) => theme.sizes?.subtitle1};
  color: ${({ theme }) => theme.colors?.greyDark};
  margin: 6px 0 6px 2px;
`;

const FileUploadButton = styled.div`
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors?.primary};
  color: ${({ theme }) => theme.colors?.primary};
  text-transform: uppercase;
  font-family: ${({ theme }) => theme.fonts?.secondary};
  font-size: ${({ theme }) => theme.sizes?.button};
  letter-spacing: 1px;
  font-weight: 500;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  box-sizing: border-box;
  min-width: 64px;
  height: 36px;
  overflow: hidden;
  vertical-align: middle;
  margin: 6px 0 0;
  cursor: pointer;
  ${mediumRoundedCorners};
`;

const FileUploadInput = styled.input`
  font-family: ${({ theme }) => theme.fonts?.secondary};
  color: ${({ theme }) => theme.colors?.blackMedium};
  display:block;
  margin-bottom: 4px;
  opacity: 0;
  height: 0;
  position: absolute;
`;

const FileUploadFileName = styled.div`
  font-family: ${({ theme }) => theme.fonts?.secondary};
  font-size: ${({ theme }) => theme.sizes?.body1};
  color: ${({ theme }) => theme.colors?.black};
  margin: 0 0 0 2px;
`;

const preventDefaultAndStopPropagation = (event) => {
  event.stopPropagation();
  event.preventDefault();
};

const FileUpload = React.forwardRef(
  ({
    label,
    ariaLabel,
    helpText,
    htmlId,
    className,
    onChange,
    filename,
    accept,
  }, reference) => (
    <FileUploadWrapper
      className={className}
      onDragOver={preventDefaultAndStopPropagation}
      onDragEnter={preventDefaultAndStopPropagation}
      onDragLeave={preventDefaultAndStopPropagation}
      onDrop={useCallback((event) => {
        preventDefaultAndStopPropagation(event);

        onChange({ target: { files: event.dataTransfer.files } });
      }, [onChange])}
    >
      <FileUploadLabel>
        {label}
        <FileUploadHelpText>
          {helpText}
        </FileUploadHelpText>
        <FileUploadButton>
          Select a file
        </FileUploadButton>
        <FileUploadInput
          id={htmlId}
          type="file"
          accept={accept}
          onChange={onChange}
          ref={reference}
          aria-label={ariaLabel}
        />
      </FileUploadLabel>
      <FileUploadFileName>
        { filename }
      </FileUploadFileName>
    </FileUploadWrapper>
  ),
);

FileUpload.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  filename: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

FileUpload.defaultProps = {
  className: undefined,
  onChange: undefined,
  htmlId: 'fileUpload',
  label: undefined,
  helpText: undefined,
  filename: 'No File Selected.',
  accept: undefined,
  ariaLabel: undefined,
};

export const FileUploadWithController = ({
  name,
  control,
  rules,
  noFileSelectedLabel,
  label,
  helpText,
  htmlId,
  accept,
  ariaLabel,
  className,
}) => {
  const render = useCallback(
    ({
      onChange, value, ref,
    }) => (
      <FileUpload
        onChange={({ target: { files } }) => onChange(files[0])}
        filename={value?.name ?? noFileSelectedLabel}
        ref={ref}
        label={label}
        helpText={helpText}
        htmlId={htmlId}
        accept={accept}
        ariaLabel={ariaLabel}
        className={className}
      />
    ),
    [
      noFileSelectedLabel,
      label,
      helpText,
      htmlId,
      accept,
      ariaLabel,
      className,
    ],
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={render}
    />
  );
};

FileUploadWithController.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  rules: PropTypes.shape({}).isRequired,
  noFileSelectedLabel: PropTypes.string,
  className: PropTypes.string,
  htmlId: PropTypes.string,
  label: PropTypes.string,
  helpText: PropTypes.string,
  accept: PropTypes.string,
  ariaLabel: PropTypes.string,
};

FileUploadWithController.defaultProps = {
  noFileSelectedLabel: 'No File Selected.',
  className: undefined,
  htmlId: undefined,
  label: undefined,
  helpText: undefined,
  accept: undefined,
  ariaLabel: undefined,
};

export default FileUpload;
